import { ApiClient } from '@center-technologies/shared-api-clients';
import { logAnalyticsEvent, checkFeatureGate } from '@center-technologies/analytics';
import { getSignedSphereImage } from './getSignedImage';

// Helper to build sphere info URL (single sphere)
function buildSphereInfoUrl(id: string): string {
  const base = process.env['REACT_APP_GRAPH_ENDPOINT'] || '';
  return base ? `${base}/spheres/${encodeURIComponent(id)}/info` : `/spheres/${encodeURIComponent(id)}/info`;
}

// Helper to build batch info URL
function buildBatchInfoUrl(): string {
  const base = process.env['REACT_APP_GRAPH_ENDPOINT'] || '';
  return base ? `${base}/spheres/batch-info` : `/spheres/batch-info`;
}

/**
 * `slots.avatar` payload shape returned by the graph-service. When the sphere
 * has been migrated to a `sphere_attachments` slot row, `signedUrl` carries a
 * short-lived SAS; when the row does not yet exist the backend synthesises a
 * `{ legacy: true }` marker from the Gremlin `imageId` so the frontend can
 * fall back to the legacy signed-image endpoint uniformly.
 */
interface SlotAvatar {
  id: string;
  signedUrl?: string;
  expiresAt?: string;
  mimeType?: string;
  legacy?: true;
}

interface SphereSlots {
  avatar?: SlotAvatar;
}

interface SphereInfo {
  sphere?: {
    id?: string;
    label?: string;
    properties?: {
      name?: string;
      /**
       * Schema system_name. The batch endpoint has always returned it
       * (`BatchResponseItem.type`) and the normaliser below used to drop it, so callers
       * needing a sphere's type had to re-fetch the whole sphere through `getSphereById`
       * — the direct-call pattern `/standards/frontend/api-clients.md` Rule 6 bans.
       * Carrying it costs nothing: it is already in the response.
       */
      type?: string;
      glyph?: string;
      glyphColor?: import('types/glyph').GlyphColor;
      imageId?: string;
      email?: string;
      /**
       * The sphere's PARTITION KEY — the organization that owns it — not the
       * sphere's own id. Returned by `POST /spheres/batch-info`; carried here so
       * callers that only need "which org is this sphere in" (the URL-org account
       * switch, for one) can answer it from the cache rather than re-fetching the
       * whole sphere via `getSphereById`. Absent on a `center_organization`
       * sphere, which is its own organization — use `sphere.id` there.
       */
      sphereId?: string;
    };
    slots?: SphereSlots;
  };
  properties?: {
    name?: string;
    type?: string;
    glyph?: string;
    glyphColor?: import('types/glyph').GlyphColor;
    imageId?: string;
    email?: string;
  };
  slots?: SphereSlots;
  /**
   * EAINT-887: roster-snapshot counts of active subjects assigned to each of
   * this sphere's perspectives, keyed by perspective `system_name`. Populated
   * by graph-service `POST /spheres/batch-info`. The WHO CHECKS IN sentence
   * reads this to render the "· N people today" segment without a follow-up
   * fetch. Absent (or unresolved) means "hide the count segment", not "zero".
   */
  perspectiveAssignmentCounts?: Record<string, number>;
}

interface CachedSphereData {
  info: SphereInfo;
  imageUrl?: string;
  thumbnailUrl?: string;
  /** The resolved avatar slot payload (slot row or legacy marker). */
  slotAvatar?: SlotAvatar;
  loadedAt: number;
  expiresAt: number;
}

interface ImageLoadResult {
  sphereId: string;
  info: SphereInfo;
  imageUrl?: string;
  thumbnailUrl?: string;
  slotAvatar?: SlotAvatar;
  expiresAt?: number;
}

interface BatchResponseItem {
  id: string;
  label: string;
  name: string;
  type: string;
  glyph: string;
  glyphColor?: import('types/glyph').GlyphColor;
  imageId: string;
  /** Partition key (owning organization); see `SphereInfo.sphere.properties.sphereId`. */
  sphereId?: string;
  slots?: SphereSlots;
  /** EAINT-887: see `SphereInfo.perspectiveAssignmentCounts`. */
  perspectiveAssignmentCounts?: Record<string, number>;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface ResolvedImageData {
  imageUrl?: string;
  thumbnailUrl?: string;
  slotAvatar?: SlotAvatar;
  expiresAt?: number;
}

/**
 * Resolve the effective image URLs for a sphere by preferring a migrated
 * `slots.avatar` row (server-minted SAS) over the legacy `imageId` → signed
 * image endpoint. The slot path short-circuits the extra network call; the
 * legacy path is used only when no slot URL is available but there's an
 * `imageId` (or the slot carries the `legacy: true` marker).
 */
async function resolveImageDataForSphere(
  sphereId: string,
  graphClient: ApiClient,
  slots: SphereSlots | undefined,
  imageId: string | undefined,
): Promise<ResolvedImageData> {
  const slotAvatar = slots?.avatar;

  if (slotAvatar?.signedUrl && !slotAvatar.legacy) {
    const parsedExpiry = slotAvatar.expiresAt ? Date.parse(slotAvatar.expiresAt) : undefined;
    return {
      imageUrl: slotAvatar.signedUrl,
      thumbnailUrl: slotAvatar.signedUrl,
      slotAvatar,
      expiresAt: Number.isFinite(parsedExpiry) ? parsedExpiry : undefined,
    };
  }

  // When the canonical-slot gate is ON, skip legacy resolution entirely.
  // Spheres without a migrated slot row appear with the schema-default icon
  // until the backfill reaches them.
  if (checkFeatureGate('feature_sphere_avatar_canonical_slot_enabled')) {
    return { slotAvatar };
  }

  const legacySource = imageId || slotAvatar?.id;
  if (!legacySource) {
    logAnalyticsEvent('sphere_avatar_legacy_resolution', undefined, {
      sphereId,
      source: 'noSlotNoLegacy',
    });
    return { slotAvatar };
  }

  logAnalyticsEvent('sphere_avatar_legacy_resolution', undefined, {
    sphereId,
    source: 'legacyImageId',
  });

  try {
    const signed = await getSignedSphereImage(graphClient, sphereId);
    return {
      imageUrl: signed.imageUrl,
      thumbnailUrl: signed.thumbnailUrl,
      slotAvatar,
      expiresAt: signed.expiresAt,
    };
  } catch (imageError) {
    console.debug(
      `Failed to load image for sphere ${sphereId}:`,
      imageError instanceof Error ? imageError.message : String(imageError),
    );
    return { slotAvatar };
  }
}

class GlobalSphereCache {
  private readonly cache = new Map<string, CachedSphereData>();
  private readonly inflightRequests = new Map<string, Promise<ImageLoadResult>>();
  /**
   * Bumped each time a sphere's entry is cleared (`epoch` when every entry is). A load sent before
   * the clear answers its callers but is not cached, so a read that raced a write never puts the
   * pre-write state back.
   */
  private readonly generations = new Map<string, number>();
  private epoch = 0;
  /**
   * Views that show a sphere (an avatar, a node), keyed by sphere id. Told when that sphere's entry
   * is cleared, which every write to its picture or glyph does, so a mounted view re-reads it
   * instead of keeping the value it read before the write.
   */
  private readonly listeners = new Map<string, Set<() => void>>();
  private readonly TTL = 10 * 60 * 1000; // 10 minutes
  private readonly BATCH_SIZE = 50;
  private readonly BATCH_DELAY = 50; // ms
  private batchQueue: Array<{ sphereId: string; resolve: (data: CachedSphereData) => void; reject: (error: Error) => void }> = [];
  private batchTimer?: NodeJS.Timeout;
  private currentGraphClient?: ApiClient;

  /**
   * Get sphere data (info + image) with intelligent batching.
   * Requests are collected in a 50ms window and sent as a single
   * POST /spheres/batch-info call instead of individual GET requests.
   */
  async getSphereData(sphereId: string, graphClient: ApiClient): Promise<CachedSphereData> {
    // Reject non-UUID IDs (e.g. "system" actor strings) to prevent 400 errors
    if (!UUID_REGEX.test(sphereId)) {
      return {
        info: { sphere: { id: sphereId } },
        loadedAt: Date.now(),
        expiresAt: Date.now() + this.TTL,
      };
    }

    const now = Date.now();

    // 1. Check cache first
    const cached = this.cache.get(sphereId);
    if (cached && cached.expiresAt > now) {
      logAnalyticsEvent('sphere_avatar_cache_hit', undefined, {
        sphereId,
        ageMs: now - cached.loadedAt,
      });
      return cached;
    }

    // 2. Add to batch queue
    return this.queueForBatch(sphereId, graphClient);
  }

  /**
   * Batch loading with smart timing
   */
  private queueForBatch(sphereId: string, graphClient: ApiClient): Promise<CachedSphereData> {
    return new Promise((resolve, reject) => {
      // Store the graph client for batch processing
      this.currentGraphClient = graphClient;

      // Always add to queue — duplicates are resolved from the same batch result
      this.batchQueue.push({ sphereId, resolve, reject });

      // Set up batch timer
      if (this.batchTimer) {
        clearTimeout(this.batchTimer);
      }

      this.batchTimer = setTimeout(() => {
        this.processBatch();
      }, this.BATCH_DELAY);

      // Process immediately if batch is full
      if (this.batchQueue.length >= this.BATCH_SIZE) {
        if (this.batchTimer) {
          clearTimeout(this.batchTimer);
          this.batchTimer = undefined;
        }
        this.processBatch();
      }
    });
  }

  /**
   * Process batched requests with a single API call.
   * Uses POST /spheres/batch-info to fetch all sphere info in one request,
   * then fetches signed images in parallel for spheres that have imageId.
   */
  private async processBatch(): Promise<void> {
    const batch = [...this.batchQueue];
    this.batchQueue = [];

    if (batch.length === 0 || !this.currentGraphClient) return;

    const graphClient = this.currentGraphClient;

    // Deduplicate sphere IDs
    const uniqueIds = [...new Set(batch.map(item => item.sphereId))];
    const sentGenerations = new Map(uniqueIds.map((id) => [id, this.generationOf(id)]));
    const settle = (result: ImageLoadResult): CachedSphereData =>
      sentGenerations.get(result.sphereId) === this.generationOf(result.sphereId)
        ? this.cacheResult(result)
        : this.toCached(result);

    console.debug(`[GlobalSphereCache] Batch fetch: ${uniqueIds.length} spheres, ${batch.length} callers`);

    const batchStart = Date.now();

    try {
      // Single batch API call
      const batchUrl = buildBatchInfoUrl();
      const response: { spheres: Record<string, BatchResponseItem> } =
        await graphClient.post(batchUrl, { ids: uniqueIds });
      const sphereMap = response?.spheres || {};

      // Convert batch response items to SphereInfo format and fetch images
      const resultMap = new Map<string, ImageLoadResult>();

      const imagePromises = uniqueIds.map(async (id) => {
        const item = sphereMap[id];
        if (!item) return;

        // Normalize flat batch response → nested SphereInfo format
        const info: SphereInfo = {
          sphere: {
            id: item.id,
            label: item.label,
            properties: {
              name: item.name,
              type: item.type,
              glyph: item.glyph,
              glyphColor: item.glyphColor,
              imageId: item.imageId,
              sphereId: item.sphereId,
            },
            slots: item.slots,
          },
          slots: item.slots,
          perspectiveAssignmentCounts: item.perspectiveAssignmentCounts,
        };

        const imageData = await resolveImageDataForSphere(id, graphClient, item.slots, item.imageId);
        resultMap.set(id, { sphereId: id, info, ...imageData });
      });

      await Promise.allSettled(imagePromises);

      logAnalyticsEvent('sphere_avatar_cache_miss_batch', undefined, {
        batchSize: uniqueIds.length,
        durationMs: Date.now() - batchStart,
      });

      // Resolve all queued callbacks
      for (const item of batch) {
        const result = resultMap.get(item.sphereId);
        if (result) {
          item.resolve(settle(result));
        } else {
          // Sphere not found in batch — resolve with empty info rather than rejecting
          const emptyResult: ImageLoadResult = {
            sphereId: item.sphereId,
            info: { sphere: { id: item.sphereId } },
          };
          item.resolve(settle(emptyResult));
        }
      }
    } catch (error) {
      // Batch call failed — fall back to individual requests
      console.warn('[GlobalSphereCache] Batch fetch failed, falling back to individual requests:', error);

      const promises = batch.map(item => this.loadSingleSphere(item.sphereId, graphClient));
      const results = await Promise.allSettled(promises);

      for (const [index, result] of results.entries()) {
        const { resolve, reject } = batch[index];
        if (result.status === 'fulfilled') {
          resolve(settle(result.value));
        } else {
          console.warn(`Failed to load sphere ${batch[index].sphereId}:`, result.reason);
          // The read's own error, so a caller can tell a refusal (403/404) from an outage by its status.
          reject(result.reason);
        }
      }
    }
  }

  /**
   * Load single sphere data (info + image) — used as fallback when batch fails
   */
  private async loadSingleSphere(sphereId: string, graphClient: ApiClient): Promise<ImageLoadResult> {
    // Prevent duplicate requests
    if (this.inflightRequests.has(sphereId)) {
      return this.inflightRequests.get(sphereId)!;
    }

    const promise = this.performSphereLoad(sphereId, graphClient);
    this.inflightRequests.set(sphereId, promise);

    try {
      const result = await promise;
      return result;
    } finally {
      this.inflightRequests.delete(sphereId);
    }
  }

  /**
   * Individual sphere load — only used when batch endpoint is unavailable
   */
  private async performSphereLoad(sphereId: string, graphClient: ApiClient): Promise<ImageLoadResult> {
    try {
      const infoUrl = buildSphereInfoUrl(sphereId);
      const info = await graphClient.get(infoUrl);

      const sphere = info?.sphere ?? info;
      const properties = sphere?.properties ?? {};
      const slots: SphereSlots | undefined = info?.slots ?? info?.sphere?.slots;

      const imageData = await resolveImageDataForSphere(
        sphereId,
        graphClient,
        slots,
        properties?.imageId,
      );

      return {
        sphereId,
        info,
        ...imageData,
      };

    } catch (error) {
      console.error(`Failed to load sphere data for ${sphereId}:`, error);
      throw error;
    }
  }

  /**
   * Calls `listener` each time `sphereId`'s entry is cleared (by `clearSphere` or `clearAll`).
   * Returns the unsubscribe. Pairs with `versionOf` for `useSyncExternalStore`.
   */
  subscribe(sphereId: string, listener: () => void): () => void {
    let set = this.listeners.get(sphereId);
    if (!set) {
      set = new Set();
      this.listeners.set(sphereId, set);
    }
    set.add(listener);
    return () => {
      const current = this.listeners.get(sphereId);
      if (!current) return;
      current.delete(listener);
      if (current.size === 0) this.listeners.delete(sphereId);
    };
  }

  /** Changes each time `sphereId`'s entry is cleared: a view that read before the change reads again. */
  versionOf(sphereId: string): string {
    return this.generationOf(sphereId);
  }

  private notify(sphereId: string): void {
    // Snapshot first: a listener may unsubscribe or subscribe during the call, and neither may change who is notified now.
    const listeners = [...(this.listeners.get(sphereId) ?? [])];
    for (const listener of listeners) listener();
  }

  private generationOf(sphereId: string): string {
    return `${this.epoch}:${this.generations.get(sphereId) ?? 0}`;
  }

  /**
   * Cache the loaded result
   */
  private cacheResult(result: ImageLoadResult): CachedSphereData {
    const cached = this.toCached(result);
    this.cache.set(result.sphereId, cached);
    return cached;
  }

  /** The cache entry a loaded result becomes. */
  private toCached(result: ImageLoadResult): CachedSphereData {
    const now = Date.now();
    // SAS-near-expiry refinement: cap cache TTL so we never serve a stale SAS.
    // If the slot SAS expires sooner than our TTL, expire at (SAS expiresAt - 60s)
    // so a SAS minted near the end of a bucket window is not served past expiry.
    const slotExpiresMs = result.slotAvatar?.expiresAt
      ? Date.parse(result.slotAvatar.expiresAt)
      : undefined;
    const expiresAt =
      slotExpiresMs !== undefined && Number.isFinite(slotExpiresMs)
        ? Math.min(now + this.TTL, slotExpiresMs - 60_000)
        : result.expiresAt ?? now + this.TTL;

    const cached: CachedSphereData = {
      info: result.info,
      imageUrl: result.imageUrl,
      thumbnailUrl: result.thumbnailUrl,
      slotAvatar: result.slotAvatar,
      loadedAt: now,
      expiresAt
    };

    return cached;
  }

  /**
   * Preload multiple spheres (for conversations, lists, etc.)
   */
  async preloadSpheres(sphereIds: string[], graphClient: ApiClient): Promise<void> {
    const uncachedIds = sphereIds.filter(id => !this.isSphereCached(id));
    if (uncachedIds.length === 0) return;

    console.debug(`[GlobalSphereCache] Preloading ${uncachedIds.length} spheres`);

    // Queue all uncached spheres — they'll be batch-fetched together
    const promises = uncachedIds.map(sphereId =>
      this.getSphereData(sphereId, graphClient).catch(error => {
        console.warn(`Failed to preload sphere ${sphereId}:`, error);
        return null;
      })
    );

    await Promise.all(promises);
  }

  /**
   * Check if sphere is cached and not expired
   */
  isSphereCached(sphereId: string): boolean {
    const cached = this.cache.get(sphereId);
    return cached ? cached.expiresAt > Date.now() : false;
  }

  /**
   * Get cached sphere synchronously (returns undefined if not cached)
   */
  getCachedSphere(sphereId: string): CachedSphereData | undefined {
    const cached = this.cache.get(sphereId);
    if (cached && cached.expiresAt > Date.now()) {
      return cached;
    }
    return undefined;
  }

  /**
   * Clear cache for specific sphere
   */
  clearSphere(sphereId: string): void {
    this.cache.delete(sphereId);
    this.inflightRequests.delete(sphereId);
    this.generations.set(sphereId, (this.generations.get(sphereId) ?? 0) + 1);
    this.notify(sphereId);
  }

  /**
   * Clear entire cache
   */
  clearAll(): void {
    this.cache.clear();
    this.generations.clear();
    this.epoch += 1;
    this.inflightRequests.clear();
    this.batchQueue = [];
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = undefined;
    }
    // Snapshot first: a listener may unsubscribe during notify, which would otherwise change the keys being walked.
    const sphereIds = [...this.listeners.keys()];
    for (const sphereId of sphereIds) this.notify(sphereId);
  }

  /**
   * Get cache stats for debugging
   */
  getStats() {
    return {
      cacheSize: this.cache.size,
      inflightRequests: this.inflightRequests.size,
      queueSize: this.batchQueue.length
    };
  }
}

// Singleton instance
export const globalSphereCache = new GlobalSphereCache();

/** @deprecated Use `globalSphereCache` instead */
export const globalSphereImageCache = globalSphereCache;

// Export types
export type { SphereInfo, CachedSphereData, SlotAvatar, SphereSlots };
